import { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { Plus, Search, Edit2, Download, Warehouse, Trash2, Loader2 } from 'lucide-react';
import { api } from '@/api/client';
import { Modal } from '@/components/ui/Modal';
import { GmailPagination } from '@/components/ui/GmailPagination';
import { WarehouseForm } from './WarehouseForm';
import { WarehouseEditHistory, WarehouseHistoryButton } from '@/components/WarehouseEditHistory';

interface Warehouse {
  id: number;
  name: string;
  code?: string;
  contactName?: string;
  contactPhone?: string;
  city?: string;
  state?: string;
  stateId?: number;
  pincode?: string;
  address?: string;
  rtoMobile?: string;
  rtoCity?: string;
  rtoState?: string;
  rtoStateId?: number;
  rtoPincode?: string;
  rtoAddress?: string;
  isActive: boolean;
}

export function MerchantWarehousesTab({ merchantId, canDelete = true }: { merchantId: number; canDelete?: boolean }) {
  const qc = useQueryClient();
  // Which pickup location's change history is open. Staff-only: the log names our own people, and
  // the merchant portal renders this tab with canDelete=false.
  const [history, setHistory] = useState<Warehouse | null>(null);
  const [search, setSearch] = useState('');
  const [showCreate, setShowCreate] = useState(false);
  const [editing, setEditing] = useState<Warehouse | null>(null);
  const [exporting, setExporting] = useState(false);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);

  const { data: warehouses, isLoading } = useQuery({
    queryKey: ['merchant-warehouses', merchantId, search],
    queryFn: async () => {
      const params: Record<string, string> = {};
      if (search) params.search = search;
      return (await api.get<Warehouse[]>(`/merchants/${merchantId}/warehouses`, { params })).data;
    },
  });

  const total = warehouses?.length ?? 0;
  // Reset to the first page whenever the result set or page size changes.
  useEffect(() => { setPage(1); }, [search, pageSize, total]);
  const paged = useMemo(
    () => (warehouses ?? []).slice((page - 1) * pageSize, page * pageSize),
    [warehouses, page, pageSize],
  );

  const del = useMutation({
    mutationFn: async (id: number) => (await api.delete(`/merchants/${merchantId}/warehouses/${id}`)).data,
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['merchant-warehouses', merchantId] }); toast.success('Pickup location deactivated'); },
    onError: (e: any) => toast.error(e?.response?.data?.message ?? 'Failed to remove'),
  });

  const handleExport = async () => {
    setExporting(true);
    try {
      const r = await api.get(`/merchants/${merchantId}/warehouses/export`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([r.data]));
      const a = document.createElement('a');
      a.href = url;
      a.download = `warehouses-${merchantId}-${new Date().toISOString().slice(0,10)}.csv`;
      document.body.appendChild(a); a.click(); a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 10_000);
    } finally { setExporting(false); }
  };

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <div className="flex-1 flex items-center gap-2 bg-slate-50 rounded-lg px-3 py-2 max-w-md">
          <Search size={16} className="text-slate-400" />
          <input type="text" placeholder="Search pickup location, code, city, contact..."
            value={search} onChange={(e) => setSearch(e.target.value)}
            className="flex-1 bg-transparent outline-none text-sm" />
        </div>
        <div className="flex items-center gap-2">
          <GmailPagination page={page} pageSize={pageSize} total={total} onPage={setPage} />
          <select value={pageSize} onChange={(e) => setPageSize(Number(e.target.value))}
            className="border border-slate-300 rounded-lg px-2 py-2 text-sm text-slate-700 bg-white outline-none hover:border-slate-400"
            title="Rows per page">
            {[10, 25, 50, 100].map((n) => <option key={n} value={n}>{n} / page</option>)}
          </select>
          <button onClick={handleExport} disabled={exporting}
            className="px-3 py-2 border border-slate-300 text-slate-700 text-sm rounded-lg font-medium hover:bg-slate-50 transition flex items-center gap-2 disabled:opacity-60">
            <Download size={14} /> {exporting ? 'Exporting...' : 'Export'}
          </button>
          <button onClick={() => setShowCreate(true)}
            className="bg-primary text-white px-3 py-2 text-sm rounded-lg font-medium hover:bg-primary-700 transition flex items-center gap-2">
            <Plus size={14} /> Add Pickup Location
          </button>
        </div>
      </div>

      <div className="border border-slate-200 rounded-lg overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-xs uppercase text-slate-500">
              <tr>
                <th className="text-left px-4 py-3">ID</th>
                <th className="text-left px-4 py-3">Pickup Location / Contact</th>
                <th className="text-left px-4 py-3">Address</th>
                <th className="text-left px-4 py-3">RTO</th>
                <th className="text-left px-4 py-3">Status</th>
                <th className="px-4 py-3" />
              </tr>
            </thead>
            <tbody>
              {isLoading && <tr><td colSpan={6} className="text-center py-8 text-slate-400">Loading...</td></tr>}
              {!isLoading && total === 0 && (
                <tr><td colSpan={6} className="text-center py-8 text-slate-400">
                  <Warehouse size={20} className="mx-auto mb-1 opacity-50" />
                  <p className="text-xs">No pickup locations yet. Add the merchant's first pickup location.</p>
                </td></tr>
              )}
              {paged.map((w) => (
                <tr key={w.id} className="border-t border-slate-100 hover:bg-slate-50">
                  <td className="px-4 py-3 font-mono text-slate-500 text-xs">#{w.id}</td>
                  <td className="px-4 py-3">
                    <div className="font-medium text-slate-900">{w.name}</div>
                    <div className="text-xs text-slate-500">{w.contactName ?? '—'}{w.contactPhone ? ` · ${w.contactPhone}` : ''}</div>
                    {w.code && <div className="text-xs"><code className="bg-slate-100 px-1.5 py-0.5 rounded">{w.code}</code></div>}
                  </td>
                  <td className="px-4 py-3 text-slate-600 text-xs max-w-xs">
                    <div>{[w.city, w.state, w.pincode].filter(Boolean).join(', ') || '—'}</div>
                    {w.address && <div className="text-slate-400 truncate">{w.address}</div>}
                  </td>
                  <td className="px-4 py-3 text-slate-600 text-xs max-w-xs">
                    {(w.rtoCity || w.rtoMobile) ? (
                      <>
                        <div>{[w.rtoCity, w.rtoState, w.rtoPincode].filter(Boolean).join(', ') || '—'}</div>
                        {w.rtoMobile && <div className="text-slate-400">{w.rtoMobile}</div>}
                      </>
                    ) : <span className="text-slate-400">Same as basic</span>}
                  </td>
                  <td className="px-4 py-3">
                    <span className={`inline-flex items-center gap-1 text-xs font-medium ${w.isActive ? 'text-success' : 'text-slate-400'}`}>
                      <span className={`w-1.5 h-1.5 rounded-full ${w.isActive ? 'bg-success' : 'bg-slate-300'}`} />
                      {w.isActive ? 'Active' : 'Inactive'}
                    </span>
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap">
                    <div className="flex items-center gap-1">
                      <button onClick={() => setEditing(w)} className="p-1.5 text-slate-400 hover:text-primary hover:bg-primary/10 rounded" title="Edit"><Edit2 size={14} /></button>
                      {canDelete && <WarehouseHistoryButton onClick={() => setHistory(w)} />}
                      {canDelete && (
                        <button onClick={() => { if (confirm(`Deactivate "${w.name}"?`)) del.mutate(w.id); }}
                          className="p-1.5 text-slate-400 hover:text-danger hover:bg-danger/10 rounded" title="Deactivate">
                          {del.isPending ? <Loader2 size={14} className="animate-spin" /> : <Trash2 size={14} />}
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <Modal isOpen={showCreate} onClose={() => setShowCreate(false)} title="Add Pickup Location" subtitle="Pickup location + RTO return address" maxWidth="3xl">
        <WarehouseForm merchantId={merchantId} onSuccess={() => setShowCreate(false)} />
      </Modal>

      <Modal isOpen={!!editing} onClose={() => setEditing(null)} title="Edit Pickup Location" subtitle={editing?.name} maxWidth="3xl">
        {editing && <WarehouseForm merchantId={merchantId} warehouseId={editing.id} defaultValues={editing} onSuccess={() => { setEditing(null); qc.invalidateQueries({ queryKey: ['warehouse-edit-logs', merchantId, editing.id] }); }} />}
      </Modal>

      {history && (
        <WarehouseEditHistory merchantId={merchantId} warehouseId={history.id}
          warehouseName={history.name} onClose={() => setHistory(null)} />
      )}
    </div>
  );
}
